// Google Food feed runs — GET /platform/google-food/feed-runs (newest
// first) and POST to request one. The POST answers 202 with a pending run;
// the outbox builds the feed (every store that opted in), archives it in
// the blob store and completes the run, so the page polls while any run is
// still pending.

import { useCallback, useEffect, useState } from 'react';
import { Play, RotateCw } from 'lucide-react';
import { api, dateTimeOf, label } from '../../lib/api';
import type { FeedRun } from './types';

const STATUS_DOT: Record<string, string> = {
  completed: 'var(--ok)',
  failed: 'var(--danger)',
  pending: 'var(--info)',
};

const PENDING_POLL_MS = 2_000;

export function FeedRuns() {
  const [runs, setRuns] = useState<FeedRun[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setRuns(await api.get<FeedRun[]>('/platform/google-food/feed-runs'));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load.');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const pending = runs?.some((r) => r.status === 'pending') ?? false;
  useEffect(() => {
    if (!pending) return;
    const t = setInterval(() => void load(), PENDING_POLL_MS);
    return () => clearInterval(t);
  }, [pending, load]);

  async function trigger() {
    setBusy(true);
    try {
      await api.post<FeedRun>('/platform/google-food/feed-runs');
      setError(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Trigger failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <div className="overline">Platform · Google Food</div>
          <h1>Feed runs</h1>
        </div>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 8 }}>
          <button className="btn btn-outline btn-sm" onClick={() => void load()}>
            <RotateCw size={13} /> Refresh
          </button>
          <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => void trigger()}>
            <Play size={13} /> {busy ? 'Requesting…' : 'Run the feed now'}
          </button>
        </span>
      </div>
      {error && <div className="err" role="alert">{error}</div>}

      {runs === null && !error && <div className="loading">Loading…</div>}
      {runs?.length === 0 && <div className="loading">No feed runs yet.</div>}
      {runs && runs.length > 0 && (
        <table className="tbl" data-section="runs">
          <thead>
            <tr>
              <th>Started</th>
              <th>Feed</th>
              <th>Run</th>
              <th>Status</th>
              <th className="num">Stores</th>
              <th>Requested by</th>
              <th>Completed</th>
              <th>Archive / error</th>
            </tr>
          </thead>
          <tbody style={{ cursor: 'default' }}>
            {runs.map((r) => (
              <tr key={r.runId} data-run={r.runId} data-status={r.status} style={{ cursor: 'default' }}>
                <td className="tm">{dateTimeOf(r.startedAt)}</td>
                <td><span className="src">{label(r.feed)}</span></td>
                <td className="docket" style={{ fontWeight: 500 }}>{r.runId}</td>
                <td>
                  <span className="chip" style={{ '--dot': STATUS_DOT[r.status] ?? 'var(--ink-3)' } as React.CSSProperties}>
                    {label(r.status)}
                  </span>
                </td>
                <td className="num money" data-store-count>{r.storeCount}</td>
                <td style={{ color: 'var(--ink-2)' }}>{r.requestedBy ?? 'schedule'}</td>
                <td style={{ color: 'var(--ink-3)' }}>{r.completedAt ? dateTimeOf(r.completedAt) : '—'}</td>
                <td style={{ color: r.error ? 'var(--danger)' : 'var(--ink-3)', fontSize: 12.5 }}>{r.error ?? r.feedObject ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
